import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import axios from 'axios';
import html2canvas from 'html2canvas';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Combobox } from '../components/ui/combobox';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { BarChart3, Building2, Layers, Truck, RefreshCw, Package, User, BarChart2, PieChart as PieChartIcon, Circle, LayoutGrid, Tags, Calendar, Download, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  PieChart, 
  Pie, 
  Cell,
  Legend,
  Treemap
} from 'recharts';
import { API } from '@/lib/apiBase';
import { filterActiveCategories } from '@/lib/categoryUtils';
import { filterProductsForDynamicLists } from '@/lib/productUtils';

const CHART_COLORS = ['#f59e0b', '#14b8a6', '#ec4899', '#8b5cf6', '#eab308', '#3b82f6', '#ef4444', '#22c55e', '#06b6d4', '#a855f7'];

// Dimension options for chart grouping
const DIMENSION_OPTIONS = [
  { value: 'company', label: 'Société', icon: Building2, color: 'text-primary' },
  { value: 'sector', label: 'Secteur', icon: Layers, color: 'text-teal-500' },
  { value: 'supplier', label: 'Fournisseur', icon: Truck, color: 'text-pink-500' },
  { value: 'product', label: 'Produit', icon: Package, color: 'text-purple-500' },
  { value: 'user', label: 'Utilisateur', icon: User, color: 'text-blue-500' },
];

// Chart type options
const CHART_TYPE_OPTIONS = [
  { value: 'bar_vertical', label: 'Barres verticales', icon: BarChart2 },
  { value: 'bar_horizontal', label: 'Barres horizontales', icon: BarChart3 },
  { value: 'pie', label: 'Camembert', icon: PieChartIcon },
  { value: 'donut', label: 'Anneau (Donut)', icon: Circle },
  { value: 'treemap', label: 'Treemap', icon: LayoutGrid },
];

export const Statistics = () => {
  const { token } = useAuth();
  const [subscriptions, setSubscriptions] = useState([]);
  const [costs, setCosts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [secteurs, setSecteurs] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedSector, setSelectedSector] = useState('');
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedUser, setSelectedUser] = useState('');

  // Date filters
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Chart dimensions (what each chart groups by)
  const [chart1Dimension, setChart1Dimension] = useState('company');
  const [chart2Dimension, setChart2Dimension] = useState('sector');
  const [chart3Dimension, setChart3Dimension] = useState('supplier');

  // Chart types
  const [chart1Type, setChart1Type] = useState('bar_vertical');
  const [chart2Type, setChart2Type] = useState('pie');
  const [chart3Type, setChart3Type] = useState('bar_horizontal');

  // Chart refs for PNG export
  const chart1Ref = useRef(null);
  const chart2Ref = useRef(null);
  const chart3Ref = useRef(null);

  // Export states
  const [exportingChart, setExportingChart] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [subsRes, costsRes, companiesRes, secteursRes, suppliersRes, categoriesRes, productsRes, usersRes] = await Promise.all([
        axios.get(`${API}/entry-lines/subscriptions`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/entry-lines/one-off`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/external/companies`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/external/secteurs`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/external/suppliers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/categories`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/products`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/external/users`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      
      setSubscriptions(subsRes.data);
      setCosts(costsRes.data);
      setCompanies(companiesRes.data);
      setSecteurs(secteursRes.data);
      setSuppliers(suppliersRes.data);
      setCategories(categoriesRes.data);
      setProducts(productsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Combobox options
  const companyOptions = useMemo(() => 
    companies.map(c => ({ value: c.id, label: c.name })),
    [companies]
  );

  const secteurOptions = useMemo(() => {
    let filtered = secteurs;
    if (selectedCompany) {
      filtered = secteurs.filter(s => s.company_id === selectedCompany);
    }
    return filtered.map(s => ({ value: s.id, label: s.name }));
  }, [secteurs, selectedCompany]);

  const supplierOptions = useMemo(() => 
    suppliers.map(s => ({ value: s.id, label: s.name })),
    [suppliers]
  );

  const categoryOptions = useMemo(() => 
    filterActiveCategories(categories).map(c => ({ value: c.id, label: c.name })),
    [categories]
  );

  const productOptions = useMemo(() => {
    let filtered = filterProductsForDynamicLists(products, categories);
    if (selectedCategory) {
      filtered = filtered.filter(p => p.category_id === selectedCategory);
    }
    return filtered.map(p => ({ value: p.id, label: p.name }));
  }, [products, categories, selectedCategory]);

  const userOptions = useMemo(() => {
    let filtered = users;
    // Filter by company if selected
    if (selectedCompany) {
      filtered = filtered.filter(u => u.company_id === selectedCompany);
    }
    // Filter by sector if selected
    if (selectedSector) {
      const sectorName = secteurs.find(s => s.id === selectedSector)?.name;
      if (sectorName) {
        filtered = filtered.filter(u => u.service === sectorName || u.sector === sectorName);
      }
    }
    return filtered.map(u => ({ 
      value: u.id, 
      label: `${u.first_name || ''} ${u.last_name || u.name || ''}`.trim() || u.username || u.email 
    }));
  }, [users, selectedCompany, selectedSector, secteurs]);

  // Year options - extract from subscription and cost data
  const yearOptions = useMemo(() => {
    const allDates = [
      ...subscriptions.map(s => s.invoice_date || s.start_date || s.created_at),
      ...costs.map(c => c.invoice_date || c.created_at)
    ].filter(Boolean);
    
    const years = [...new Set(allDates.map(d => new Date(d).getFullYear()))]
      .filter(y => !isNaN(y))
      .sort((a, b) => b - a); // Most recent first
    
    return years.map(y => ({ value: y.toString(), label: y.toString() }));
  }, [subscriptions, costs]);

  // Month options
  const monthOptions = [
    { value: '1', label: 'Janvier' },
    { value: '2', label: 'Février' },
    { value: '3', label: 'Mars' },
    { value: '4', label: 'Avril' },
    { value: '5', label: 'Mai' },
    { value: '6', label: 'Juin' },
    { value: '7', label: 'Juillet' },
    { value: '8', label: 'Août' },
    { value: '9', label: 'Septembre' },
    { value: '10', label: 'Octobre' },
    { value: '11', label: 'Novembre' },
    { value: '12', label: 'Décembre' },
  ];

  // Helper function to check if a date matches the date filters
  const matchesDateFilter = (dateStr) => {
    if (!dateStr) return true;
    
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return true;
    
    // If custom date range is set, it takes priority
    if (startDate || endDate) {
      if (startDate && date < new Date(startDate)) return false;
      if (endDate && date > new Date(endDate + 'T23:59:59')) return false;
      return true;
    }
    
    // Otherwise, use year/month filters
    if (selectedYear) {
      if (date.getFullYear() !== parseInt(selectedYear)) return false;
    }
    if (selectedMonth) {
      if ((date.getMonth() + 1) !== parseInt(selectedMonth)) return false;
    }
    
    return true;
  };

  // Filter data based on selections
  const filteredSubscriptions = useMemo(() => {
    return subscriptions.filter(sub => {
      if (selectedCompany) {
        const companyMatch = sub.company_id === selectedCompany || 
                            sub.company_name === companies.find(c => c.id === selectedCompany)?.name;
        if (!companyMatch) return false;
      }
      if (selectedSector) {
        const sectorMatch = sub.sector_id === selectedSector || 
                           sub.sector_name === secteurs.find(s => s.id === selectedSector)?.name;
        if (!sectorMatch) return false;
      }
      if (selectedSupplier) {
        const supplierMatch = sub.supplier_name === suppliers.find(s => s.id === selectedSupplier)?.name;
        if (!supplierMatch) return false;
      }
      if (selectedCategory) {
        const categoryMatch = sub.category_id === selectedCategory || 
                             sub.category_name === categories.find(c => c.id === selectedCategory)?.name;
        if (!categoryMatch) return false;
      }
      if (selectedProduct) {
        const productMatch = sub.product_id === selectedProduct || 
                            sub.product_name === products.find(p => p.id === selectedProduct)?.name;
        if (!productMatch) return false;
      }
      if (selectedUser) {
        const user = users.find(u => u.id === selectedUser);
        const userName = user ? `${user.first_name || ''} ${user.last_name || user.name || ''}`.trim() : '';
        const userMatch = sub.user_id === selectedUser || sub.user_name === userName;
        if (!userMatch) return false;
      }
      // Date filter
      if (!matchesDateFilter(sub.invoice_date || sub.start_date || sub.created_at)) {
        return false;
      }
      return true;
    });
  }, [subscriptions, selectedCompany, selectedSector, selectedSupplier, selectedCategory, selectedProduct, selectedUser, companies, secteurs, suppliers, categories, products, users, selectedYear, selectedMonth, startDate, endDate]);

  const filteredCosts = useMemo(() => {
    return costs.filter(cost => {
      if (selectedCompany) {
        const companyMatch = cost.company_id === selectedCompany || 
                            cost.company_name === companies.find(c => c.id === selectedCompany)?.name;
        if (!companyMatch) return false;
      }
      if (selectedSector) {
        const sectorMatch = cost.sector_id === selectedSector || 
                           cost.sector_name === secteurs.find(s => s.id === selectedSector)?.name;
        if (!sectorMatch) return false;
      }
      if (selectedSupplier) {
        const supplierMatch = cost.supplier_name === suppliers.find(s => s.id === selectedSupplier)?.name;
        if (!supplierMatch) return false;
      }
      if (selectedCategory) {
        const categoryMatch = cost.category_id === selectedCategory || 
                             cost.category_name === categories.find(c => c.id === selectedCategory)?.name;
        if (!categoryMatch) return false;
      }
      if (selectedProduct) {
        const productMatch = cost.product_id === selectedProduct || 
                            cost.product_name === products.find(p => p.id === selectedProduct)?.name;
        if (!productMatch) return false;
      }
      if (selectedUser) {
        const user = users.find(u => u.id === selectedUser);
        const userName = user ? `${user.first_name || ''} ${user.last_name || user.name || ''}`.trim() : '';
        const userMatch = cost.user_id === selectedUser || cost.user_name === userName;
        if (!userMatch) return false;
      }
      // Date filter
      if (!matchesDateFilter(cost.invoice_date || cost.created_at)) {
        return false;
      }
      return true;
    });
  }, [costs, selectedCompany, selectedSector, selectedSupplier, selectedCategory, selectedProduct, selectedUser, companies, secteurs, suppliers, categories, products, users, selectedYear, selectedMonth, startDate, endDate]);

  // Calculate statistics
  const stats = useMemo(() => {
    const activeSubs = filteredSubscriptions;
    
    let monthlySubCost = 0;
    for (const sub of activeSubs) {
      const amount = sub.amount_ht || 0;
      const cycle = sub.billing_cycle || 'monthly';
      if (cycle === 'yearly') {
        monthlySubCost += amount / 12;
      } else if (cycle === 'quarterly') {
        monthlySubCost += amount / 3;
      } else {
        monthlySubCost += amount;
      }
    }

    const totalOneOff = filteredCosts.reduce((sum, c) => sum + (c.amount_ht || 0), 0);

    // Calculate evolution vs previous month using the same filters (except date filters)
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;
    const prevYear = currentMonth === 0 ? currentYear - 1 : currentYear;

    // Helper function to check if item matches non-date filters
    const matchesNonDateFilters = (item) => {
      if (selectedCompany) {
        const companyMatch = item.company_id === selectedCompany || 
                            item.company_name === companies.find(c => c.id === selectedCompany)?.name;
        if (!companyMatch) return false;
      }
      if (selectedSector) {
        const sectorMatch = item.sector_id === selectedSector || 
                           item.sector_name === secteurs.find(s => s.id === selectedSector)?.name;
        if (!sectorMatch) return false;
      }
      if (selectedSupplier) {
        const supplierMatch = item.supplier_name === suppliers.find(s => s.id === selectedSupplier)?.name;
        if (!supplierMatch) return false;
      }
      if (selectedCategory) {
        const categoryMatch = item.category_id === selectedCategory || 
                             item.category_name === categories.find(c => c.id === selectedCategory)?.name;
        if (!categoryMatch) return false;
      }
      if (selectedProduct) {
        const productMatch = item.product_id === selectedProduct || 
                            item.product_name === products.find(p => p.id === selectedProduct)?.name;
        if (!productMatch) return false;
      }
      if (selectedUser) {
        const user = users.find(u => u.id === selectedUser);
        const userName = user ? `${user.first_name || ''} ${user.last_name || user.name || ''}`.trim() : '';
        const userMatch = item.user_id === selectedUser || item.user_name === userName;
        if (!userMatch) return false;
      }
      return true;
    };

    // Combine all data (subscriptions + one-off costs) and apply non-date filters
    const allFilteredData = [...subscriptions, ...costs].filter(matchesNonDateFilters);
    
    // Calculate total for current month
    const currentMonthTotal = allFilteredData
      .filter(item => {
        const date = new Date(item.invoice_date || item.start_date || item.created_at);
        return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
      })
      .reduce((sum, item) => sum + (item.amount_ht || 0), 0);

    // Calculate total for previous month
    const prevMonthTotal = allFilteredData
      .filter(item => {
        const date = new Date(item.invoice_date || item.start_date || item.created_at);
        return date.getMonth() === prevMonth && date.getFullYear() === prevYear;
      })
      .reduce((sum, item) => sum + (item.amount_ht || 0), 0);

    // Calculate percentage evolution
    let evolutionPercent = 0;
    if (prevMonthTotal > 0) {
      evolutionPercent = ((currentMonthTotal - prevMonthTotal) / prevMonthTotal) * 100;
    } else if (currentMonthTotal > 0) {
      evolutionPercent = 100; // If no previous month data but current month has data
    }

    return {
      total_subscriptions: filteredSubscriptions.length,
      active_subscriptions: activeSubs.length,
      monthly_subscription_cost: monthlySubCost,
      total_one_off_costs: totalOneOff,
      total_costs_count: filteredCosts.length,
      evolution_percent: evolutionPercent,
      current_month_total: currentMonthTotal,
      prev_month_total: prevMonthTotal
    };
  }, [filteredSubscriptions, filteredCosts, subscriptions, costs, selectedCompany, selectedSector, selectedSupplier, selectedCategory, selectedProduct, selectedUser, companies, secteurs, suppliers, categories, products, users]);

  // Generic function to calculate data by any dimension
  const calculateByDimension = useMemo(() => {
    return (dimension) => {
      const dimStats = {};
      
      // Get the field name for the dimension
      const getFieldValue = (item, dim) => {
        switch (dim) {
          case 'company':
            return item.company_name || 'Non assigné';
          case 'sector':
            return item.sector_name || 'Non assigné';
          case 'supplier':
            return item.supplier_name || 'Non assigné';
          case 'product':
            return item.product_name || 'Non assigné';
          case 'user':
            return item.user_name || 'Non assigné';
          default:
            return 'Non assigné';
        }
      };

      for (const sub of filteredSubscriptions) {
        const key = getFieldValue(sub, dimension);
        if (!dimStats[key]) {
          dimStats[key] = { subscriptions: 0, costs: 0, total: 0 };
        }
        dimStats[key].subscriptions += sub.amount_ht || 0;
        dimStats[key].total += sub.amount_ht || 0;
      }
      
      for (const cost of filteredCosts) {
        const key = getFieldValue(cost, dimension);
        if (!dimStats[key]) {
          dimStats[key] = { subscriptions: 0, costs: 0, total: 0 };
        }
        dimStats[key].costs += cost.amount_ht || 0;
        dimStats[key].total += cost.amount_ht || 0;
      }
      
      return Object.entries(dimStats)
        .map(([name, data]) => ({ name, ...data }))
        .sort((a, b) => b.total - a.total);
    };
  }, [filteredSubscriptions, filteredCosts]);

  // Calculate data for each chart based on selected dimension
  const chart1Data = useMemo(() => calculateByDimension(chart1Dimension), [calculateByDimension, chart1Dimension]);
  const chart2Data = useMemo(() => calculateByDimension(chart2Dimension), [calculateByDimension, chart2Dimension]);
  const chart3Data = useMemo(() => calculateByDimension(chart3Dimension), [calculateByDimension, chart3Dimension]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(value);
  };

  const resetFilters = () => {
    setSelectedCompany('');
    setSelectedSector('');
    setSelectedSupplier('');
    setSelectedCategory('');
    setSelectedProduct('');
    setSelectedUser('');
    setSelectedYear('');
    setSelectedMonth('');
    setStartDate('');
    setEndDate('');
  };

  // Clear year/month when date range is set (only if both are set)
  const handleStartDateChange = (value) => {
    setStartDate(value);
    if (value) {
      setSelectedYear('');
      setSelectedMonth('');
    }
  };

  const handleEndDateChange = (value) => {
    setEndDate(value);
    if (value) {
      setSelectedYear('');
      setSelectedMonth('');
    }
  };

  const handleYearChange = (value) => {
    setSelectedYear(value);
    if (value) {
      setStartDate('');
      setEndDate('');
    }
  };

  const handleMonthChange = (value) => {
    setSelectedMonth(value);
    if (value) {
      setStartDate('');
      setEndDate('');
    }
  };

  // Reset sector when company changes
  useEffect(() => {
    if (selectedCompany) {
      const sectorBelongsToCompany = secteurs.some(
        s => s.id === selectedSector && s.company_id === selectedCompany
      );
      if (!sectorBelongsToCompany) {
        setSelectedSector('');
      }
    }
  }, [selectedCompany, secteurs, selectedSector]);

  // Reset user when company or sector changes (cascade filter)
  useEffect(() => {
    if (selectedUser && (selectedCompany || selectedSector)) {
      const user = users.find(u => u.id === selectedUser);
      if (user) {
        // Check if user belongs to selected company
        if (selectedCompany && user.company_id !== selectedCompany) {
          setSelectedUser('');
          return;
        }
        // Check if user belongs to selected sector
        if (selectedSector) {
          const sectorName = secteurs.find(s => s.id === selectedSector)?.name;
          if (sectorName && user.service !== sectorName && user.sector !== sectorName) {
            setSelectedUser('');
          }
        }
      }
    }
  }, [selectedCompany, selectedSector, users, secteurs, selectedUser]);

  // Reset product when category changes (cascade filter)
  useEffect(() => {
    if (selectedCategory) {
      const productBelongsToCategory = products.some(
        p => p.id === selectedProduct && p.category_id === selectedCategory
      );
      if (!productBelongsToCategory) {
        setSelectedProduct('');
      }
    }
  }, [selectedCategory, products, selectedProduct]);

  // Get dimension config
  const getDimensionConfig = (dimension) => {
    return DIMENSION_OPTIONS.find(d => d.value === dimension) || DIMENSION_OPTIONS[0];
  };

  // Export chart as PNG
  const exportChartAsPNG = useCallback(async (chartRef, chartNum, dimension) => {
    if (!chartRef.current) return;
    
    setExportingChart(chartNum);
    try {
      const canvas = await html2canvas(chartRef.current, {
        backgroundColor: '#1f2937', // Dark background
        scale: 2, // Higher quality
        logging: false,
        useCORS: true,
      });
      
      const link = document.createElement('a');
      const dimensionLabel = getDimensionConfig(dimension).label;
      const date = new Date().toISOString().split('T')[0];
      link.download = `statistiques_${dimensionLabel.toLowerCase()}_${date}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      
      toast.success(`Graphique "${dimensionLabel}" exporté en PNG`);
    } catch (error) {
      console.error('Error exporting chart:', error);
      toast.error('Erreur lors de l\'export du graphique');
    } finally {
      setExportingChart(null);
    }
  }, []);

  // Dimension selector component
  const DimensionSelector = ({ value, onChange, chartId }) => {
    return (
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="w-[160px] h-8 text-sm" data-testid={`dimension-select-${chartId}`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {DIMENSION_OPTIONS.map(option => {
            const OptionIcon = option.icon;
            return (
              <SelectItem key={option.value} value={option.value}>
                <div className="flex items-center gap-2">
                  <OptionIcon className={`w-4 h-4 ${option.color}`} />
                  {option.label}
                </div>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    );
  };

  // Chart type selector component
  const ChartTypeSelector = ({ value, onChange, chartId }) => {
    return (
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="w-[170px] h-8 text-sm" data-testid={`charttype-select-${chartId}`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {CHART_TYPE_OPTIONS.map(option => {
            const OptionIcon = option.icon;
            return (
              <SelectItem key={option.value} value={option.value}>
                <div className="flex items-center gap-2">
                  <OptionIcon className="w-4 h-4 text-muted-foreground" />
                  {option.label}
                </div>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    );
  };

  // Custom Treemap content
  const CustomTreemapContent = ({ x, y, width, height, name, total, index }) => {
    const fill = CHART_COLORS[index % CHART_COLORS.length];
    const displayName = name && name.length > 12 ? name.substring(0, 12) + '...' : name;
    
    return (
      <g>
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          style={{
            fill,
            stroke: 'hsl(var(--background))',
            strokeWidth: 2,
          }}
        />
        {width > 45 && height > 28 && (
          <>
            <text
              x={x + width / 2}
              y={y + height / 2 - 6}
              textAnchor="middle"
              fill="#fff"
              fontSize={9}
              fontWeight="bold"
            >
              {displayName}
            </text>
            <text
              x={x + width / 2}
              y={y + height / 2 + 8}
              textAnchor="middle"
              fill="#fff"
              fontSize={8}
            >
              {formatCurrency(total)}
            </text>
          </>
        )}
      </g>
    );
  };

  // Render chart based on type
  const renderChart = (data, chartType, chartId) => {
    if (data.length === 0) {
      return (
        <div className="h-[300px] flex items-center justify-center text-muted-foreground">
          Aucune donnée disponible
        </div>
      );
    }

    const chartData = data.slice(0, 15);

    switch (chartType) {
      case 'bar_vertical':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis 
                dataKey="name" 
                tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 9 }}
                axisLine={{ stroke: 'hsl(var(--border))' }}
                angle={-45}
                textAnchor="end"
                height={70}
                interval={0}
                tickFormatter={(value) => value.length > 12 ? value.substring(0, 12) + '...' : value}
              />
              <YAxis 
                tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 9 }}
                axisLine={{ stroke: 'hsl(var(--border))' }}
                tickFormatter={(value) => `${Math.round(value)}€`}
                width={50}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'hsl(var(--card))', 
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px',
                  color: 'hsl(var(--foreground))',
                  fontSize: '11px'
                }}
                formatter={(value) => [formatCurrency(value), '']}
              />
              <Legend wrapperStyle={{ fontSize: '10px' }} />
              <Bar dataKey="subscriptions" name="Abonnements" fill="#f59e0b" radius={[4, 4, 0, 0]} />
              <Bar dataKey="costs" name="Coûts ponctuels" fill="#14b8a6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        );

      case 'bar_horizontal':
        return (
          <ResponsiveContainer width="100%" height={Math.max(300, chartData.length * 32)}>
            <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis 
                type="number"
                tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 9 }}
                axisLine={{ stroke: 'hsl(var(--border))' }}
                tickFormatter={(value) => `${Math.round(value)}€`}
              />
              <YAxis 
                type="category"
                dataKey="name" 
                tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 9 }}
                axisLine={{ stroke: 'hsl(var(--border))' }}
                width={100}
                tickFormatter={(value) => value.length > 15 ? value.substring(0, 15) + '...' : value}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'hsl(var(--card))', 
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px',
                  color: 'hsl(var(--foreground))',
                  fontSize: '11px'
                }}
                formatter={(value) => [formatCurrency(value), '']}
              />
              <Legend wrapperStyle={{ fontSize: '10px' }} />
              <Bar dataKey="subscriptions" name="Abonnements" fill="#f59e0b" radius={[0, 4, 4, 0]} />
              <Bar dataKey="costs" name="Coûts ponctuels" fill="#ec4899" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        );

      case 'pie':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
              <Pie
                data={chartData.slice(0, 8)}
                cx="50%"
                cy="45%"
                outerRadius={80}
                dataKey="total"
                nameKey="name"
                label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                labelLine={false}
              >
                {chartData.slice(0, 8).map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#1f2937', 
                  border: '1px solid #374151',
                  borderRadius: '8px',
                  fontSize: '11px'
                }}
                itemStyle={{ color: '#f3f4f6' }}
                labelStyle={{ color: '#f3f4f6', fontWeight: 'bold' }}
                formatter={(value, name, props) => [formatCurrency(value), props.payload.name]}
              />
              <Legend 
                layout="horizontal" 
                verticalAlign="bottom" 
                align="center"
                wrapperStyle={{ fontSize: '9px', paddingTop: '10px' }}
                formatter={(value, entry) => {
                  const name = entry.payload.name;
                  return name.length > 18 ? name.substring(0, 18) + '...' : name;
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        );

      case 'donut':
        const totalValue = chartData.reduce((sum, item) => sum + item.total, 0);
        return (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
              <Pie
                data={chartData.slice(0, 8)}
                cx="50%"
                cy="45%"
                innerRadius={50}
                outerRadius={80}
                paddingAngle={2}
                dataKey="total"
                nameKey="name"
                label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                labelLine={false}
              >
                {chartData.slice(0, 8).map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#1f2937', 
                  border: '1px solid #374151',
                  borderRadius: '8px',
                  fontSize: '11px'
                }}
                itemStyle={{ color: '#f3f4f6' }}
                labelStyle={{ color: '#f3f4f6', fontWeight: 'bold' }}
                formatter={(value, name, props) => [formatCurrency(value), props.payload.name]}
              />
              <Legend 
                layout="horizontal" 
                verticalAlign="bottom" 
                align="center"
                wrapperStyle={{ fontSize: '9px', paddingTop: '10px' }}
                formatter={(value, entry) => {
                  const name = entry.payload.name;
                  return name.length > 18 ? name.substring(0, 18) + '...' : name;
                }}
              />
              {/* Center text */}
              <text x="50%" y="42%" textAnchor="middle" fill="hsl(var(--foreground))" fontSize={10}>
                Total
              </text>
              <text x="50%" y="50%" textAnchor="middle" fill="hsl(var(--foreground))" fontSize={11} fontWeight="bold">
                {formatCurrency(totalValue)}
              </text>
            </PieChart>
          </ResponsiveContainer>
        );

      case 'treemap':
        const treemapData = chartData.map((item, index) => ({
          ...item,
          size: item.total,
          index
        }));
        return (
          <ResponsiveContainer width="100%" height={300}>
            <Treemap
              data={treemapData}
              dataKey="size"
              nameKey="name"
              aspectRatio={4/3}
              stroke="hsl(var(--background))"
              content={<CustomTreemapContent />}
            >
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'hsl(var(--card))', 
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px',
                  color: 'hsl(var(--foreground))'
                }}
                formatter={(value, name, props) => [formatCurrency(props.payload.total), props.payload.name]}
              />
            </Treemap>
          </ResponsiveContainer>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="statistics-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground flex items-center gap-3">
            <BarChart3 className="w-8 h-8 text-primary" />
            Statistiques
          </h1>
          <p className="text-muted-foreground mt-1">
            Analysez vos coûts par société, secteur, fournisseur, produit et utilisateur
          </p>
        </div>
        <Button 
          variant="outline" 
          onClick={resetFilters}
          className="border-border text-foreground"
          data-testid="reset-filters-button"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Réinitialiser les filtres
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div className="space-y-2">
              <label className="text-sm text-muted-foreground flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                Société
              </label>
              <Combobox
                options={companyOptions}
                value={selectedCompany}
                onValueChange={setSelectedCompany}
                placeholder="Toutes les sociétés"
                searchPlaceholder="Rechercher une société..."
                emptyText="Aucune société trouvée"
                data-testid="filter-company"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm text-muted-foreground flex items-center gap-2">
                <Layers className="w-4 h-4" />
                Secteur
              </label>
              <Combobox
                options={secteurOptions}
                value={selectedSector}
                onValueChange={setSelectedSector}
                placeholder="Tous les secteurs"
                searchPlaceholder="Rechercher un secteur..."
                emptyText={selectedCompany ? "Aucun secteur pour cette société" : "Aucun secteur trouvé"}
                data-testid="filter-sector"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm text-muted-foreground flex items-center gap-2">
                <User className="w-4 h-4" />
                Utilisateur
              </label>
              <Combobox
                options={userOptions}
                value={selectedUser}
                onValueChange={setSelectedUser}
                placeholder="Tous les utilisateurs"
                searchPlaceholder="Rechercher un utilisateur..."
                emptyText={(selectedCompany || selectedSector) ? "Aucun utilisateur pour cette sélection" : "Aucun utilisateur trouvé"}
                data-testid="filter-user"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm text-muted-foreground flex items-center gap-2">
                <Truck className="w-4 h-4" />
                Fournisseur
              </label>
              <Combobox
                options={supplierOptions}
                value={selectedSupplier}
                onValueChange={setSelectedSupplier}
                placeholder="Tous les fournisseurs"
                searchPlaceholder="Rechercher un fournisseur..."
                emptyText="Aucun fournisseur trouvé"
                data-testid="filter-supplier"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm text-muted-foreground flex items-center gap-2">
                <Tags className="w-4 h-4" />
                Catégorie
              </label>
              <Combobox
                options={categoryOptions}
                value={selectedCategory}
                onValueChange={setSelectedCategory}
                placeholder="Toutes les catégories"
                searchPlaceholder="Rechercher une catégorie..."
                emptyText="Aucune catégorie trouvée"
                data-testid="filter-category"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm text-muted-foreground flex items-center gap-2">
                <Package className="w-4 h-4" />
                Produit
              </label>
              <Combobox
                options={productOptions}
                value={selectedProduct}
                onValueChange={setSelectedProduct}
                placeholder="Tous les produits"
                searchPlaceholder="Rechercher un produit..."
                emptyText={selectedCategory ? "Aucun produit pour cette catégorie" : "Aucun produit trouvé"}
                data-testid="filter-product"
              />
            </div>
          </div>

          {/* Date Filters - Second Row */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4 pt-4 border-t border-border">
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Année
              </Label>
              <Select value={selectedYear || "all"} onValueChange={(v) => handleYearChange(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="filter-year">
                  <SelectValue placeholder="Toutes les années" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les années</SelectItem>
                  {yearOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Mois
              </Label>
              <Select value={selectedMonth || "all"} onValueChange={(v) => handleMonthChange(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="filter-month">
                  <SelectValue placeholder="Tous les mois" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les mois</SelectItem>
                  {monthOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Du</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => handleStartDateChange(e.target.value)}
                data-testid="filter-start-date"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Au</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => handleEndDateChange(e.target.value)}
                data-testid="filter-end-date"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-card border-border" data-testid="stat-evolution">
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground">Évolution vs Mois Précédent</p>
            <div className="flex items-center gap-2 mt-2">
              {stats.evolution_percent > 0 ? (
                <TrendingUp className="w-8 h-8 text-red-500" />
              ) : stats.evolution_percent < 0 ? (
                <TrendingDown className="w-8 h-8 text-green-500" />
              ) : (
                <Minus className="w-8 h-8 text-muted-foreground" />
              )}
              <p className={`text-3xl font-bold font-mono ${
                stats.evolution_percent > 0 
                  ? 'text-red-500' 
                  : stats.evolution_percent < 0 
                    ? 'text-green-500' 
                    : 'text-foreground'
              }`}>
                {stats.evolution_percent > 0 ? '+' : ''}{stats.evolution_percent.toFixed(1)}%
              </p>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {formatCurrency(stats.current_month_total)} ce mois vs {formatCurrency(stats.prev_month_total)} le mois dernier
            </p>
          </CardContent>
        </Card>

        <Card className="bg-card border-border" data-testid="stat-monthly-cost">
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground">Coût Mensuel Abo.</p>
            <p className="text-3xl font-bold text-foreground mt-2 font-mono">
              {formatCurrency(stats.monthly_subscription_cost)}
            </p>
          </CardContent>
        </Card>

        <Card className="bg-card border-border" data-testid="stat-total-costs">
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground">Coûts Ponctuels</p>
            <p className="text-3xl font-bold text-foreground mt-2 font-mono">
              {formatCurrency(stats.total_one_off_costs)}
            </p>
            <Badge className="mt-2 bg-teal-500/20 text-teal-400">
              {stats.total_costs_count} entrées
            </Badge>
          </CardContent>
        </Card>

        <Card className="bg-card border-border" data-testid="stat-total-global">
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground">Total Global</p>
            <p className="text-3xl font-bold text-foreground mt-2 font-mono">
              {formatCurrency(stats.monthly_subscription_cost + stats.total_one_off_costs)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Chart 1 */}
        <Card className="bg-card border-border" data-testid="chart-1">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-foreground font-heading text-lg">
              Graphique 1
            </CardTitle>
            <div className="flex items-center gap-2">
              <DimensionSelector 
                value={chart1Dimension} 
                onChange={setChart1Dimension}
                chartId="1"
              />
              <ChartTypeSelector
                value={chart1Type}
                onChange={setChart1Type}
                chartId="1"
              />
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => exportChartAsPNG(chart1Ref, '1', chart1Dimension)}
                disabled={exportingChart === '1'}
                title="Télécharger en PNG"
                data-testid="export-chart-1"
              >
                <Download className={`w-4 h-4 ${exportingChart === '1' ? 'animate-pulse' : ''}`} />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div ref={chart1Ref}>
              {renderChart(chart1Data, chart1Type, '1')}
            </div>
          </CardContent>
        </Card>

        {/* Chart 2 */}
        <Card className="bg-card border-border" data-testid="chart-2">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-foreground font-heading text-lg">
              Graphique 2
            </CardTitle>
            <div className="flex items-center gap-2">
              <DimensionSelector 
                value={chart2Dimension} 
                onChange={setChart2Dimension}
                chartId="2"
              />
              <ChartTypeSelector
                value={chart2Type}
                onChange={setChart2Type}
                chartId="2"
              />
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => exportChartAsPNG(chart2Ref, '2', chart2Dimension)}
                disabled={exportingChart === '2'}
                title="Télécharger en PNG"
                data-testid="export-chart-2"
              >
                <Download className={`w-4 h-4 ${exportingChart === '2' ? 'animate-pulse' : ''}`} />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div ref={chart2Ref}>
              {renderChart(chart2Data, chart2Type, '2')}
            </div>
          </CardContent>
        </Card>

        {/* Chart 3 */}
        <Card className="bg-card border-border lg:col-span-2" data-testid="chart-3">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-foreground font-heading text-lg">
              Graphique 3
            </CardTitle>
            <div className="flex items-center gap-2">
              <DimensionSelector 
                value={chart3Dimension} 
                onChange={setChart3Dimension}
                chartId="3"
              />
              <ChartTypeSelector
                value={chart3Type}
                onChange={setChart3Type}
                chartId="3"
              />
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => exportChartAsPNG(chart3Ref, '3', chart3Dimension)}
                disabled={exportingChart === '3'}
                title="Télécharger en PNG"
                data-testid="export-chart-3"
              >
                <Download className={`w-4 h-4 ${exportingChart === '3' ? 'animate-pulse' : ''}`} />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div ref={chart3Ref}>
              {renderChart(chart3Data, chart3Type, '3')}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
